import {AxiosInstance} from "axios";
import Invoice from "../model/invoice/Invoice";
import {InvoiceEmailApiDto, InvoiceEventApiDto} from "./model/rest";
import {default as InvoiceEvent, InvoiceEventType, InvoiceEventTypeEnum} from "../model/invoice/event/InvoiceEvent";
import InvoiceCreatedEvent from "../model/invoice/event/InvoiceCreatedEvent";
import InvoiceSentEvent from "../model/invoice/event/InvoiceSentEvent";
import InvoicePaidEvent from "../model/invoice/event/InvoicePaidEvent";
import InvoiceUpdatedEvent from "../model/invoice/event/InvoiceUpdatedEvent";
import InvoiceApprovedEvent from "../model/invoice/event/InvoiceApprovedEvent";
import InvoicePartsAndLabourDetails from "../model/invoice/InvoicePartsAndLabourDetails";
import {Option} from "ts-option";
import InvoiceSummary from "../model/invoice/InvoiceSummary";
import DictionaryCache from "../helper/DictionaryCache";
import InvoicePaymentReleaseData from "../model/invoice/InvoicePaymentReleaseData";

/**
 * Invoice Api.
 */
export default class InvoiceApi {
	/**
	 * Cache for searching invoices.
	 * It prevents unnecessary request to the server.
	 */
	invoicesCache: DictionaryCache<InvoiceSummary[]>;

	constructor(public readonly axiosInstance: AxiosInstance) {
		this.invoicesCache = DictionaryCache.empty();
	}

	/**
	 * Fetch invoices for partner brand. //TODO: delete, it's unused (make sure it won't be)
	 * @returns {Promise<Invoice[]>} Promise containing all invoices for partner brand.
	 */
	fetchInvoices(partnerBrandId: string): Promise<Invoice[]> {
		const url = `/api/web/accounting/partner_brands/${partnerBrandId}/invoices`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(Invoice.fromJson));
	}

	/**
	 * Fetch invoice for job.
	 * @param {string} jobId
	 * @returns {Promise<Invoice>}
	 */
	fetchInvoice(jobId: string): Promise<Invoice> {
		const url = `/api/web/accounting/jobs/${jobId}/invoice`;
		console.warn(`url request turned off: /api/web/accounting/jobs/${jobId}/invoice`);
		return Promise.reject();//this.axiosInstance.get(url).then(resp => Invoice.fromJson(resp.data));
	}

	/**
	 * Fetch invoice parts and labour details.
	 * @param {string} invoiceId
	 * @returns {Promise<InvoicePartsAndLabourDetails>}
	 */
	fetchInvoicePartsAndLabourDetails(invoiceId: string): Promise<InvoicePartsAndLabourDetails> {
		const url = `/api/web/accounting/invoices/${invoiceId}/parts_labour_item_details`;
		return this.axiosInstance.get(url).then(resp => InvoicePartsAndLabourDetails.fromJson(resp.data));
	}

	/**
	 * Update invoice.
	 * @param {string} invoiceId
	 * @param {Invoice} data
	 * @param {InvoicePartsAndLabourDetails} labourAndPartsDetails
	 * @returns {Promise<Invoice>}
	 */
	updateInvoice(invoiceId: string,
		data: Invoice,
		labourAndPartsDetails: Option<InvoicePartsAndLabourDetails>): Promise<Invoice> {
		let json = data.toJson();
		labourAndPartsDetails.map(ad => {
			json = {
				...json,
				partsAndLabourItemDetails: ad.toJson()
			}
		});
		const url = `/api/web/accounting/invoices/${invoiceId}`;
		return this.axiosInstance.put(url, json).then(resp => Invoice.fromJson(resp.data));
	}

	approveInvoice(invoiceId: string): Promise<Invoice> {
		const url = `/api/web/accounting/invoices/${invoiceId}/approve`;
		return this.axiosInstance.put(url).then(resp => Invoice.fromJson(resp.data));
	}

	/**
	 * Send invoice email.
	 * @param invoiceId
	 * @param customerEmail
	 * @param subject
	 */
	sendInvoiceEmail(invoiceId: string, customerEmail: string, subject: string): Promise<any> {
		const url = `/api/web/accounting/invoices/${invoiceId}/email`;
		return this.axiosInstance.put(url, {customerEmail, subject});
	}

	/**
	 * Fetch events for invoice.
	 * @param {string} invoiceId
	 * @returns {Promise<Array<InvoiceEventType>>}
	 */
	fetchInvoiceEvents(invoiceId: string): Promise<Array<InvoiceEventType>> {
		const url = `/api/web/accounting/invoices/${invoiceId}/events`;
		return this.axiosInstance.get(url).then(resp => resp.data.map((event: InvoiceEventApiDto) => {
			switch (event.type) {
				case InvoiceEventTypeEnum.InvoiceCreated:
					return InvoiceCreatedEvent.fromJson(event);
				case InvoiceEventTypeEnum.InvoiceSent:
					return InvoiceSentEvent.fromJson(event);
				case InvoiceEventTypeEnum.InvoicePaid:
					return InvoicePaidEvent.fromJson(event);
				case InvoiceEventTypeEnum.InvoiceUpdated:
					return InvoiceUpdatedEvent.fromJson(event);
				case InvoiceEventTypeEnum.InvoiceApproved:
					return InvoiceApprovedEvent.fromJson(event);
				default:
					//fallback
					return null;
			}
		}).filter((item: InvoiceEvent) => item !== null));
	}

	fetchEmailPreview(invoiceId: string): Promise<InvoiceEmailApiDto> {
		const url = `/api/web/accounting/invoices/${invoiceId}/email_preview`;
		return this.axiosInstance.get(url).then(resp => resp.data);
	}

	/**
	 * Confirm full invoice payment.
	 * @param {string} invoiceId
	 * @returns {Promise<any>}
	 */
	confirmFullInvoicePayment(invoiceId: string): Promise<any> {
		const url = `/api/web/accounting/invoices/${invoiceId}/confirm_full_payment`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Find invoices by text.
	 * @param text text using to search.
	 * @returns promise with results.
	 */
	findInvoicesByText(text: string): Promise<InvoiceSummary[]> {
		return new Promise((resolve, reject) => {
			const cachedData = this.invoicesCache.get(text);
			if (cachedData.isDefined)
				resolve(cachedData.get);
			else {
				const url = `/api/web/accounting/invoices/search`;
				this.axiosInstance.get(url, {params: {text}}).then(resp => {
					const data = resp.data.map(InvoiceSummary.fromJson);
					this.invoicesCache.store(text, data);
					resolve(data);
				}).catch(reject);
			}
		});
	}

	/**
	 * Clear invoice search cache.
	 */
	clearInvoicesCache() {
		this.invoicesCache = DictionaryCache.empty();
	}

	/**
	 * Fetch invoice payment release data.
	 * @param {string} invoiceId
	 * @returns {Promise<InvoicePaymentReleaseData>}
	 */
	fetchPaymentReleaseData(invoiceId: string): Promise<InvoicePaymentReleaseData> {
		const url = `/api/web/accounting/invoices/${invoiceId}/payment_release_data`;
		return this.axiosInstance.get(url).then(resp => InvoicePaymentReleaseData.fromJson(resp.data));
	}

	/**
	 * Release partner payment.
	 * @param {string} invoiceId
	 * @returns {AxiosPromise<any>}
	 */
	releasePartnerPayment(invoiceId: string) {
		const url = `/api/web/accounting/invoices/${invoiceId}/release_partner_payment`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Release hiber payment.
	 * @param {string} invoiceId
	 * @returns {AxiosPromise<any>}
	 */
	releaseHiberPayment(invoiceId: string) {
		const url = `/api/web/accounting/invoices/${invoiceId}/release_hiber_payment`;
		return this.axiosInstance.put(url);
	}
}
